import React from 'react';
import Joyride from 'react-joyride';

const TutorialOverlay = ({
  currentTutorialId,
  currentTutorialSteps,
  tutorialRun,
  tutorialStepIndex,
  tutorialReadyToAdvance,
  tutorialAutoAdvancePaused,
  tutorialStepCountdown,
  tutorialShortcutOpen,
  tutorialShortcutDock,
  tutorialShortcutPanelRef,
  tutorialSessionKey,
  safeJoyrideSteps,
  handleTutorialCallback,
  onToggleAutoAdvancePaused,
  onJumpToTutorialStep,
  onJumpToLogisticsTutorial,
  onQueueRouteCreatorTutorial,
}) => {
  const tutorialPrimaryLabelBase =
    tutorialStepIndex >= currentTutorialSteps.length - 1 ? 'Finalizar' : 'Siguiente';
  const tutorialPrimaryLabel = tutorialReadyToAdvance
    ? `${tutorialPrimaryLabelBase}${tutorialAutoAdvancePaused ? ' · Pausa' : ` · ${Math.max(tutorialStepCountdown, 0)}s`}`
    : `${tutorialPrimaryLabelBase} · audio`;

  return (
    <>
      <Joyride
        key={`${currentTutorialId}-${tutorialSessionKey}`}
        run={tutorialRun}
        stepIndex={tutorialStepIndex}
        steps={safeJoyrideSteps}
        callback={handleTutorialCallback}
        continuous
        showProgress
        showSkipButton
        scrollToFirstStep
        disableScrolling={false}
        disableOverlayClose
        locale={{ back: 'Atrás', close: 'Cerrar', last: tutorialPrimaryLabel, next: tutorialPrimaryLabel, skip: 'Omitir' }}
        styles={{
          options: {
            arrowColor: '#020617',
            backgroundColor: '#020617',
            overlayColor: 'rgba(2,6,23,0.62)',
            primaryColor: '#22d3ee',
            textColor: '#e2e8f0',
            width: 440,
            zIndex: 2000,
          },
          buttonNext: {
            backgroundColor: '#22d3ee',
            color: '#082f49',
            fontWeight: 700,
            borderRadius: '14px',
            boxShadow: '0 10px 24px rgba(34, 211, 238, 0.28)',
          },
          buttonBack: {
            color: '#cbd5e1',
          },
          buttonSkip: {
            color: '#94a3b8',
          },
          tooltip: {
            borderRadius: 24,
            boxShadow: '0 28px 80px rgba(2, 6, 23, 0.52)',
            border: '1px solid rgba(34, 211, 238, 0.18)',
            background: 'linear-gradient(145deg, rgba(2,6,23,0.98), rgba(15,23,42,0.98))',
          },
          tooltipTitle: {
            color: '#f8fafc',
            fontWeight: 800,
            fontSize: '1rem',
            letterSpacing: '-0.02em',
          },
          tooltipContent: {
            color: '#cbd5e1',
            fontSize: '0.93rem',
            lineHeight: 1.6,
          },
          spotlight: {
            borderRadius: 28,
            boxShadow: '0 0 0 2px rgba(34, 211, 238, 0.5), 0 0 38px rgba(34, 211, 238, 0.18)',
          },
        }}
      />

      {tutorialRun ? (
        <>
          <div className="fixed bottom-4 left-1/2 z-[2100] flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-cyan-300/25 bg-slate-950/92 px-2.5 py-2 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl md:hidden">
            <button
              type="button"
              onClick={onToggleAutoAdvancePaused}
              className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-full border text-[11px] font-black uppercase tracking-[0.16em] transition ${
                tutorialAutoAdvancePaused
                  ? 'border-amber-300/40 bg-amber-400/15 text-amber-100'
                  : 'border-cyan-300/35 bg-cyan-400/12 text-cyan-100'
              }`}
            >
              P
            </button>
            <div className="flex items-center gap-1 overflow-x-auto">
              {currentTutorialSteps.map((step, index) => (
                <button
                  key={`mobile-${currentTutorialId}-${step.title}-${index}`}
                  type="button"
                  onClick={() => onJumpToTutorialStep(index)}
                  className={`inline-flex h-8 min-w-[2rem] items-center justify-center rounded-full border text-[11px] font-black transition ${
                    index === tutorialStepIndex
                      ? 'border-cyan-300/60 bg-cyan-400/18 text-cyan-50 shadow-[0_0_0_1px_rgba(103,232,249,0.22)]'
                      : 'border-slate-700/80 bg-slate-900/80 text-slate-300'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>

          <div
            ref={tutorialShortcutPanelRef}
            className={`fixed z-[2100] hidden w-[18rem] flex-col gap-2 transition-all duration-300 md:flex ${
              tutorialShortcutDock === 'top-left'
                ? 'left-5 top-24'
                : tutorialShortcutDock === 'bottom-right'
                  ? 'bottom-5 right-5'
                  : tutorialShortcutDock === 'bottom-left'
                    ? 'bottom-5 left-5'
                    : 'right-5 top-24'
            }`}
          >
            <button
              type="button"
              onClick={onToggleAutoAdvancePaused}
              className="inline-flex items-center gap-2 rounded-2xl border border-cyan-300/40 bg-slate-950/90 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-cyan-100 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl transition hover:-translate-y-[1px]"
            >
              <span className={`inline-flex h-2.5 w-2.5 rounded-full ${tutorialAutoAdvancePaused ? 'bg-amber-300' : 'bg-emerald-300 animate-pulse'}`} />
              {tutorialAutoAdvancePaused ? 'Reanudar' : 'Pausar'}
            </button>
            <div className={`overflow-hidden rounded-2xl border border-cyan-300/20 bg-slate-950/88 shadow-[0_18px_40px_rgba(2,6,23,0.42)] backdrop-blur-xl transition-all duration-300 ${tutorialShortcutOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="border-b border-slate-800/80 px-4 py-3">
                <p className="text-[10px] font-black uppercase tracking-[0.18em] text-cyan-200">Accesos del tutorial</p>
                <p className="mt-1 text-xs text-slate-400">Podés ir directo al paso que necesites o retomar el recorrido desde donde te convenga.</p>
              </div>
              <div className="flex max-h-[50vh] flex-col gap-1 overflow-y-auto p-2">
                {currentTutorialSteps.map((step, index) => (
                  <button
                    key={`${currentTutorialId}-${step.title}-${index}`}
                    type="button"
                    onClick={() => onJumpToTutorialStep(index)}
                    className={`rounded-xl border px-3 py-2 text-left text-sm transition ${
                      index === tutorialStepIndex
                        ? 'border-cyan-300/60 bg-cyan-400/18 text-cyan-50 shadow-[0_0_0_1px_rgba(103,232,249,0.22),0_10px_30px_rgba(8,145,178,0.18)]'
                        : 'border-transparent text-slate-300 hover:border-slate-700 hover:bg-slate-800/80 hover:text-white'
                    }`}
                  >
                    <span className={`block text-[10px] font-bold uppercase tracking-[0.16em] ${index === tutorialStepIndex ? 'text-cyan-200' : 'text-slate-500'}`}>Paso {index + 1}</span>
                    <span className="mt-0.5 block font-semibold">{step.title}</span>
                  </button>
                ))}
                {currentTutorialId === 'dashboard' ? (
                  <button
                    type="button"
                    onClick={onJumpToLogisticsTutorial}
                    className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-left text-sm font-semibold text-cyan-100 transition hover:bg-cyan-500/16"
                  >
                    <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-300/80">Siguiente sección</span>
                    <span className="mt-0.5 block">Logística</span>
                  </button>
                ) : null}
                {currentTutorialId === 'logistics' ? (
                  <button
                    type="button"
                    onClick={onQueueRouteCreatorTutorial}
                    className="rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-left text-sm font-semibold text-cyan-100 transition hover:bg-cyan-500/16"
                  >
                    <span className="block text-[10px] font-bold uppercase tracking-[0.16em] text-cyan-300/80">Siguiente sección</span>
                    <span className="mt-0.5 block">Crear ruta</span>
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </>
  );
};

export default TutorialOverlay;
